import { useState } from 'react';
import { Banner, Box, InlineStack, Stack, Text, TextField, TextLabel } from '@customerio/pluma-components/react';
import { formEventFieldsToJson, type FormEventField } from '../utils/form-event-data';

/**
 * The data of a test form submission, one row per attribute.
 *
 * The attributes are the form's own, so the keys are fixed and only the
 * values are typed. The rows that describe the form itself are shown as text.
 *
 * The editor owns its rows, like the simple editor does: it reads the rows it
 * opens on once, and from then on writes the JSON they describe on every
 * keystroke.
 */

type FormEventDataEditorProps = {
  /** The rows the editor opens on. Read once. */
  initialFields: readonly FormEventField[];
  onChange: (data: string) => void;
  /** Why the data will not be sent, once a send has been tried. */
  error: string | undefined;
  isDisabled: boolean;
};

export function FormEventDataEditor({ initialFields, onChange, error, isDisabled }: FormEventDataEditorProps) {
  const [fields, setFields] = useState<readonly FormEventField[]>(initialFields);

  const editValue = (index: number, value: string) => {
    const next = fields.map((field, at) => (at === index ? { ...field, value } : field));
    setFields(next);
    onChange(formEventFieldsToJson(next));
  };

  return (
    <Stack gap="150">
      {fields.map((field, index) => (
        // The rows are fixed: none is added, removed or moved, and two form
        // fields can share a key, so the position is what tells them apart.
        // oxlint-disable-next-line react/no-array-index-key -- rows are fixed in place; keys may repeat
        <Stack key={index}>
          <InlineStack gap="150" alignBlock="center">
            <TextLabel flex={1} maxW="full" minW="0">
              Attribute
            </TextLabel>
            <TextLabel flex={1} maxW="full" minW="0">
              Value
            </TextLabel>
          </InlineStack>
          <InlineStack gap="150" alignBlock="center" alignInline="space-between">
            <Box flex={1} maxW="full" minW="0">
              <Text>{field.key}</Text>
            </Box>
            <Box flex={1} maxW="full" minW="0">
              {field.isLocked ? (
                <Text>{field.value}</Text>
              ) : (
                // A typed answer is the workspace's own customer data; keep
                // it out of session replay.
                <TextField
                  className="fs-mask"
                  ariaLabel={`${field.key} value`}
                  placeholder="Value"
                  value={field.value}
                  isDisabled={isDisabled}
                  onInput={(event) => editValue(index, event.target.value)}
                />
              )}
            </Box>
          </InlineStack>
        </Stack>
      ))}

      {error == null ? null : <Banner variant="critical" description={error} />}
    </Stack>
  );
}
